
import { useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import type { User } from "@supabase/supabase-js";
import type { Signature } from "@/types/supabase";
import { SignatureService } from "@/services/SignatureService";

export const useSignatureMutations = (
  user: User | null,
  signatures: Signature[],
  setSignatures: React.Dispatch<React.SetStateAction<Signature[]>>
) => {
  const { toast } = useToast();
  const signatureService = new SignatureService(user);

  const saveSignature = useCallback(async (text: string, svgContent: string) => {
    if (!user) return;

    const optimisticSignature: Signature = {
      id: crypto.randomUUID(),
      text,
      svg_content: svgContent,
      user_id: user.id,
      created_at: new Date().toISOString(),
    };

    // Optimistically add the signature immediately
    setSignatures(current => [optimisticSignature, ...current]);

    try {
      // Save signature in parallel with showing the toast
      const savedSignaturePromise = signatureService.saveSignature(text, svgContent);

      // Show toast independently
      toast({
        title: "Success",
        description: "Signature saved successfully",
      });

      // Wait for the actual save to complete
      const savedSignature = await savedSignaturePromise;
      
      // Update with the real signature
      setSignatures(current => 
        current.map(sig => 
          sig.id === optimisticSignature.id ? savedSignature : sig
        )
      );

      return savedSignature;
    } catch (error) {
      console.error('[useSignatureMutations] Error saving signature:', error);
      
      // Remove optimistic signature if save failed
      setSignatures(current => 
        current.filter(sig => sig.id !== optimisticSignature.id)
      );
      
      toast({
        title: "Error",
        description: "Failed to save signature",
        variant: "destructive",
      });
      throw error;
    }
  }, [user, setSignatures, signatureService, toast]);

  const deleteSignature = useCallback(async (signatureId: string) => {
    if (!user) return;

    const signatureToDelete = signatures.find(sig => sig.id === signatureId);
    if (!signatureToDelete) return;

    // Optimistically remove the signature immediately
    setSignatures(current => current.filter(sig => sig.id !== signatureId));

    try {
      // Delete signature in parallel with showing the toast
      const deletePromise = signatureService.deleteSignature(signatureId);

      // Show toast independently
      toast({
        title: "Success",
        description: "Signature deleted successfully",
      });

      // Wait for the actual deletion to complete
      await deletePromise;
    } catch (error) {
      console.error('[useSignatureMutations] Error deleting signature:', error);
      
      // Restore the signature if delete failed
      setSignatures(current => [...current, signatureToDelete]);
      
      toast({
        title: "Error",
        description: "Failed to delete signature",
        variant: "destructive",
      });
    }
  }, [user, signatures, setSignatures, signatureService, toast]);

  return {
    saveSignature,
    deleteSignature
  };
};
